/**
 * Conflict Detection API
 *
 * Provides functions for detecting and managing task assignment conflicts
 * including temporal overlaps, ignored conflicts tracking, and conflict analysis.
 * UPDATED: Skip recurring occurrences with no assignedUserId to prevent false conflicts from unassigned tasks.
 * UPDATED: Now includes recurring task occurrences in conflict detection
 * UPDATED: Now includes service_id from template relation for immediate service selection
 * UPDATED: Fixed conflict label for reassigned recurring occurrences via batch user name fetch
 * UPDATED: Removed `as any` casts by adding typed row interfaces
 * CHANGE: Removed all debug console statements for production
 * UPDATED: Empty catch in getTaskOccurrences now logs console.error with task id and error
 * PERF: Added batch loading of occurrence exceptions (eliminates N+1 queries)
 * PERF: Added preloadedTasks parameter to detectConflicts to avoid double-fetching with execution layer
 */

import { supabase, getFullName } from './supabase';
import { parseDate } from './dateUtils';
import { calculateOccurrences, mergeWithExceptions, batchLoadOccurrenceExceptions } from './occurrences';
import type { AssignedTaskOccurrence } from './supabase';
import { captureException } from './sentry';

export interface TaskConflict {
  task1: {
    id: string;
    taskName: string;
    scheduledTime: string;
    duration: number;
    serviceId: string | null;
  };
  task2: {
    id: string;
    taskName: string;
    scheduledTime: string;
    duration: number;
    serviceId: string | null;
  };
  userId: string;
  userName: string;
  conflictDate: string;
  overlapMinutes: number;
  isIgnored: boolean;
}

export interface IgnoredConflict {
  id: string;
  task_id_1: string;
  task_id_2: string;
  user_id: string;
  conflict_date: string;
  ignored_by: string;
  ignored_at: string;
  site_id: string;
}

interface UserNameRecord {
  first_name: string;
  last_name: string;
}

interface DirectTaskRow {
  id: string;
  assigned_to_user_id: string;
  scheduled_time: string | null;
  duration_override: number | null;
  template_name: string | null;
  template_estimated_duration_minutes: number | null;
  template_service_id: string | null;
  user: { id: string; first_name: string; last_name: string } | null;
}

interface RecurringTaskRow {
  id: string;
  assigned_to_user_id: string;
  site_id: string;
  template_id: string | null;
  assigned_date: string;
  scheduled_time: string | null;
  duration_override: number | null;
  is_recurring: boolean;
  recurrence_rule_id: string | null;
  recurrence_start_date: string | null;
  recurrence_end_date: string | null;
  reassignment_from_date: string | null;
  reassignment_user_id: string | null;
  reassignment_scheduled_time: string | null;
  template_name: string | null;
  template_estimated_duration_minutes: number | null;
  template_service_id: string | null;
  user: { id: string; first_name: string; last_name: string } | null;
  recurrence: unknown;
}

/**
 * Internal interface for unified task representation (direct + recurring)
 */
interface UnifiedTask {
  id: string;
  assigned_to_user_id: string;
  scheduled_time: string;
  duration: number;
  template_name: string;
  template_service_id: string | null;
  user_first_name: string;
  user_last_name: string;
}

/**
 * Staging entry used while collecting recurring occurrences before user name resolution
 */
interface StagingOccurrence {
  id: string;
  assignedUserId: string;
  scheduledTime: string;
  duration: number;
  templateName: string;
  templateServiceId: string | null;
}

/**
 * Minimal preloaded task shape accepted by detectConflicts.
 * Matches the subset of AssignedTaskWithDetails fields needed here.
 */
export interface PreloadedTaskForConflict {
  id: string;
  assigned_to_user_id: string;
  site_id: string;
  assigned_date: string;
  scheduled_time: string | null;
  duration_override_minutes: number | null;
  is_recurring: boolean;
  parentTaskId?: string;
  occurrenceDate?: string;
  template: {
    name: string;
    estimated_duration_minutes: number;
    service_id: string | null;
  };
  assigned_to_user?: { id: string; full_name: string; username: string } | null;
}

/**
 * Calculate time overlap between two tasks
 */
function calculateOverlap(
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): number {
  const overlapStart = new Date(Math.max(start1.getTime(), start2.getTime()));
  const overlapEnd = new Date(Math.min(end1.getTime(), end2.getTime()));

  if (overlapStart >= overlapEnd) {
    return 0;
  }

  return Math.round((overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60));
}

/**
 * Parse time string (HH:MM) and combine with date
 */
function parseDateTime(dateStr: string, timeStr: string): Date {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date(dateStr);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * Build the unified task list from preloaded tasks (already assembled by execution layer).
 * This path avoids all DB queries for task/occurrence data.
 */
function buildUnifiedTasksFromPreloaded(
  tasks: PreloadedTaskForConflict[],
  date: string
): UnifiedTask[] {
  const result: UnifiedTask[] = [];

  for (const task of tasks) {
    if (!task.scheduled_time) continue;
    if (task.assigned_date !== date) continue;

    const nameParts = task.assigned_to_user?.full_name?.split(' ') || ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const taskId = task.is_recurring && task.parentTaskId && task.occurrenceDate
      ? `${task.parentTaskId}_${task.occurrenceDate}`
      : task.id;

    result.push({
      id: taskId,
      assigned_to_user_id: task.assigned_to_user_id,
      scheduled_time: task.scheduled_time,
      duration: task.duration_override_minutes ?? task.template.estimated_duration_minutes,
      template_name: task.template.name,
      template_service_id: task.template.service_id,
      user_first_name: firstName,
      user_last_name: lastName,
    });
  }

  return result;
}

/**
 * Detect conflicts for a specific date and site.
 *
 * @param siteId - The site to check
 * @param date - The date to check (YYYY-MM-DD)
 * @param preloadedTasks - Optional: tasks already loaded by the execution layer for this
 *   (siteId, date) pair. When provided, all DB fetches for tasks and occurrences are
 *   skipped and the conflict detection runs purely in-memory.
 */
export async function detectConflicts(
  siteId: string,
  date: string,
  preloadedTasks?: PreloadedTaskForConflict[]
): Promise<TaskConflict[]> {

  let allTasks: UnifiedTask[];

  if (preloadedTasks) {
    // Fast path: reuse tasks already loaded by the execution layer
    allTasks = buildUnifiedTasksFromPreloaded(preloadedTasks, date);
  } else {
    // Slow path: fetch tasks from DB (used when called standalone)
    allTasks = await fetchAndBuildUnifiedTasks(siteId, date);
  }

  // Get ignored conflicts
  const { data: ignoredConflicts, error: ignoredError } = await supabase
    .from('ignored_task_conflicts')
    .select('*')
    .eq('site_id', siteId)
    .eq('conflict_date', date);

  if (ignoredError) {
    throw ignoredError;
  }

  const ignoredSet = new Set(
    (ignoredConflicts || []).map(ic =>
      `${ic.user_id}-${[ic.task_id_1, ic.task_id_2].sort().join('-')}`
    )
  );

  // Check each pair of tasks for conflicts
  const conflicts: TaskConflict[] = [];

  for (let i = 0; i < allTasks.length; i++) {
    for (let j = i + 1; j < allTasks.length; j++) {
      const task1 = allTasks[i];
      const task2 = allTasks[j];

      if (task1.assigned_to_user_id !== task2.assigned_to_user_id) {
        continue;
      }

      const start1 = parseDateTime(date, task1.scheduled_time);
      const end1 = new Date(start1.getTime() + task1.duration * 60000);
      const start2 = parseDateTime(date, task2.scheduled_time);
      const end2 = new Date(start2.getTime() + task2.duration * 60000);

      const overlap = calculateOverlap(start1, end1, start2, end2);

      if (overlap > 0) {
        const conflictKey = `${task1.assigned_to_user_id}-${[task1.id, task2.id].sort().join('-')}`;
        const isIgnored = ignoredSet.has(conflictKey);

        const userName = getFullName({
          first_name: task1.user_first_name,
          last_name: task1.user_last_name
        });

        conflicts.push({
          task1: {
            id: task1.id,
            taskName: task1.template_name,
            scheduledTime: task1.scheduled_time,
            duration: task1.duration,
            serviceId: task1.template_service_id
          },
          task2: {
            id: task2.id,
            taskName: task2.template_name,
            scheduledTime: task2.scheduled_time,
            duration: task2.duration,
            serviceId: task2.template_service_id
          },
          userId: task1.assigned_to_user_id,
          userName: userName || 'Utilisateur inconnu',
          conflictDate: date,
          overlapMinutes: overlap,
          isIgnored
        });
      }
    }
  }

  return conflicts;
}

/**
 * Standalone DB fetch path for detectConflicts (used when preloadedTasks is not provided).
 * PERF: Uses batchLoadOccurrenceExceptions to eliminate N+1 queries for recurring tasks.
 */
async function fetchAndBuildUnifiedTasks(siteId: string, date: string): Promise<UnifiedTask[]> {
  // Get direct (non-recurring) tasks for the date
  const { data: directTasksRaw, error: directError } = await supabase
    .from('assigned_tasks')
    .select(`
      id,
      assigned_to_user_id,
      scheduled_time,
      duration_override,
      template_name,
      template_estimated_duration_minutes,
      template_service_id,
      user:users!assigned_tasks_assigned_to_user_id_fkey (
        id,
        first_name,
        last_name
      )
    `)
    .eq('site_id', siteId)
    .eq('assigned_date', date)
    .eq('is_recurring', false)
    .not('assigned_to_user_id', 'is', null)
    .not('scheduled_time', 'is', null);

  if (directError) {
    throw directError;
  }

  const directTasks = (directTasksRaw || []) as unknown as DirectTaskRow[];

  // Get recurring tasks for this site
  const { data: recurringTasksRaw, error: recurringError } = await supabase
    .from('assigned_tasks')
    .select(`
      id,
      assigned_to_user_id,
      site_id,
      template_id,
      assigned_date,
      scheduled_time,
      duration_override,
      is_recurring,
      recurrence_rule_id,
      recurrence_start_date,
      recurrence_end_date,
      reassignment_from_date,
      reassignment_user_id,
      reassignment_scheduled_time,
      template_name,
      template_estimated_duration_minutes,
      template_service_id,
      user:users!assigned_tasks_assigned_to_user_id_fkey (
        id,
        first_name,
        last_name
      ),
      recurrence:recurrence_rules!assigned_tasks_recurrence_rule_id_fkey (*)
    `)
    .eq('site_id', siteId)
    .eq('is_recurring', true)
    .not('recurrence_rule_id', 'is', null)
    .lte('recurrence_start_date', date);

  if (recurringError) {
    throw recurringError;
  }

  const recurringTasks = (recurringTasksRaw || []) as unknown as RecurringTaskRow[];

  // Build a user name cache seeded from all users already present in the query results.
  const userNameCache = new Map<string, UserNameRecord>();

  directTasks.forEach(task => {
    if (task.user) {
      userNameCache.set(task.assigned_to_user_id, {
        first_name: task.user.first_name,
        last_name: task.user.last_name
      });
    }
  });

  recurringTasks.forEach(task => {
    if (task.user) {
      userNameCache.set(task.assigned_to_user_id, {
        first_name: task.user.first_name,
        last_name: task.user.last_name
      });
    }
  });

  const allTasks: UnifiedTask[] = [];

  // Add direct tasks
  directTasks.forEach(task => {
    if (task.scheduled_time) {
      const nameRecord = userNameCache.get(task.assigned_to_user_id);
      allTasks.push({
        id: task.id,
        assigned_to_user_id: task.assigned_to_user_id,
        scheduled_time: task.scheduled_time,
        duration: task.duration_override || task.template_estimated_duration_minutes || 0,
        template_name: task.template_name || 'Mission sans nom',
        template_service_id: task.template_service_id || null,
        user_first_name: nameRecord?.first_name || '',
        user_last_name: nameRecord?.last_name || ''
      });
    }
  });

  // PERF: Batch load all occurrence exceptions in a single query (eliminates N+1)
  const recurringTaskIds = recurringTasks.map(t => t.id);
  const targetDate = parseDate(date);
  const exceptionsMap = await batchLoadOccurrenceExceptions(recurringTaskIds, targetDate, targetDate);

  // Collect recurring occurrences into a staging array so we can resolve
  // user names for reassigned occurrences in a single batch query afterwards.
  const stagingOccurrences: StagingOccurrence[] = [];

  for (const task of recurringTasks) {
    const rule = Array.isArray(task.recurrence) ? (task.recurrence as unknown[])[0] : task.recurrence;

    if (!rule) {
      continue;
    }

    if (task.recurrence_end_date && date > task.recurrence_end_date) {
      continue;
    }

    try {
      const preloadedExceptions = exceptionsMap.get(task.id) || [];

      // Use synchronous calculateOccurrences + mergeWithExceptions directly
      // since exceptions are already loaded — no async needed
      const calculated = calculateOccurrences(
        task as Parameters<typeof calculateOccurrences>[0],
        rule as Parameters<typeof calculateOccurrences>[1],
        { startDate: targetDate, endDate: targetDate }
      );

      const occurrences = mergeWithExceptions(
        calculated,
        preloadedExceptions as AssignedTaskOccurrence[],
        task as Parameters<typeof mergeWithExceptions>[2]
      );

      occurrences.forEach(occurrence => {
        if (!occurrence.isCancelled && occurrence.scheduledTime && occurrence.assignedUserId) {
          stagingOccurrences.push({
            id: `${task.id}_${occurrence.date}`,
            assignedUserId: occurrence.assignedUserId,
            scheduledTime: occurrence.scheduledTime,
            duration: occurrence.duration || task.duration_override || task.template_estimated_duration_minutes || 0,
            templateName: task.template_name || 'Mission sans nom',
            templateServiceId: task.template_service_id || null
          });
        }
      });
    } catch (e) {
      if (import.meta.env.DEV) {
        console.error('[conflictDetection] occurrence calculation failed for task', task.id, e);
      }
      captureException(e, { context: 'conflictDetection.occurrenceCalculation', taskId: task.id });
    }
  }

  // Find occurrence assignees whose names are not yet in the cache
  const unknownUserIds = new Set<string>();
  for (const entry of stagingOccurrences) {
    if (!userNameCache.has(entry.assignedUserId)) {
      unknownUserIds.add(entry.assignedUserId);
    }
  }

  // Batch-fetch the missing names in a single query.
  if (unknownUserIds.size > 0) {
    const { data: extraUsers } = await supabase
      .from('users')
      .select('id, first_name, last_name')
      .in('id', [...unknownUserIds]);

    (extraUsers || []).forEach((u: { id: string; first_name: string; last_name: string }) => {
      userNameCache.set(u.id, { first_name: u.first_name, last_name: u.last_name });
    });
  }

  // Push staging occurrences into allTasks using the resolved user names.
  for (const entry of stagingOccurrences) {
    const nameRecord = userNameCache.get(entry.assignedUserId);
    allTasks.push({
      id: entry.id,
      assigned_to_user_id: entry.assignedUserId,
      scheduled_time: entry.scheduledTime,
      duration: entry.duration,
      template_name: entry.templateName,
      template_service_id: entry.templateServiceId,
      user_first_name: nameRecord?.first_name || '',
      user_last_name: nameRecord?.last_name || ''
    });
  }

  return allTasks;
}

/**
 * Ignore a specific conflict
 */
export async function ignoreConflict(
  siteId: string,
  taskId1: string,
  taskId2: string,
  userId: string,
  conflictDate: string
): Promise<void> {
  const { error } = await supabase
    .from('ignored_task_conflicts')
    .insert({
      task_id_1: taskId1,
      task_id_2: taskId2,
      user_id: userId,
      conflict_date: conflictDate,
      site_id: siteId,
      ignored_by: (await supabase.auth.getUser()).data.user?.id
    });

  if (error) {
    throw error;
  }
}

/**
 * Unignore a conflict (remove from ignored list)
 */
export async function unignoreConflict(
  siteId: string,
  taskId1: string,
  taskId2: string,
  userId: string,
  conflictDate: string
): Promise<void> {
  const [id1, id2] = [taskId1, taskId2].sort();

  const { error } = await supabase
    .from('ignored_task_conflicts')
    .delete()
    .eq('site_id', siteId)
    .eq('user_id', userId)
    .eq('conflict_date', conflictDate)
    .or(`and(task_id_1.eq.${id1},task_id_2.eq.${id2}),and(task_id_1.eq.${id2},task_id_2.eq.${id1})`);

  if (error) {
    throw error;
  }
}

/**
 * Get conflict statistics for a date range
 */
export async function getConflictStats(
  siteId: string,
  startDate: string,
  endDate: string
): Promise<{
  totalConflicts: number;
  activeConflicts: number;
  ignoredConflicts: number;
  affectedUsers: number;
}> {
  const dates: string[] = [];
  const current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }

  let totalConflicts = 0;
  let activeConflicts = 0;
  let ignoredConflicts = 0;
  const affectedUserIds = new Set<string>();

  for (const date of dates) {
    const conflicts = await detectConflicts(siteId, date);
    totalConflicts += conflicts.length;

    conflicts.forEach(conflict => {
      if (conflict.isIgnored) {
        ignoredConflicts++;
      } else {
        activeConflicts++;
      }
      affectedUserIds.add(conflict.userId);
    });
  }

  return {
    totalConflicts,
    activeConflicts,
    ignoredConflicts,
    affectedUsers: affectedUserIds.size
  };
}
